import React from "react";
import {
  fetchLatestDraftsByTaktSection,
  fetchPullPlanTaktSections,
  fetchPullPlanWBSTree,
  deletePullPlan,
  fetchTaktBoard,
  getPullPlan,
  listPullPlans,
} from "../../../api/clientV2";
import type {
  PullPlan,
  PullPlanKind,
  PullPlanMode,
  PullPlanTaktSectionRef,
  PullPlanWBSNode,
  TaktBoardResponse,
  TaktSection,
  TaktSectionDraftRef,
} from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import { apiErrorMessage } from "../../../utils/apiError";
import NewPlanFromWbsPanel from "./NewPlanFromWbsPanel/NewPlanFromWbsPanel";
import SectionCard from "./SectionCard/SectionCard";
import DiscardDraftModal from "../SectionView/DiscardDraftModal/DiscardDraftModal";
import {
  emptySummaryFromRef,
  summarizeSection,
  type SectionCardSummary,
} from "./summarizeSection";
import { plainPlanCards } from "./plainPlanCards";
import "./PullPlanSectionsTab.css";

interface Props {
  scheduleId: number;
  /** Bump this from the parent to force a re-fetch (e.g. after returning
   *  from the canvas where a draft may have been created or discarded). */
  refreshNonce?: number;
  onStartPull: (sectionName: string, mode: PullPlanMode) => void;
  onContinuePull: (sectionName: string, draftId: number) => void;
  /** A new plan was created in the New-plan-from-WBS panel — route into the
   *  workspace on that draft. `anchorWbsId` is the draft's section anchor (the
   *  first pulled leaf), needed because a fresh section name resolves no WBS
   *  ids of its own yet. `kind` decides Takt-section vs plain-WBS open. */
  onLeafPlanCreated: (args: {
    sectionName: string;
    draftId: number;
    anchorWbsId: number;
    kind: PullPlanKind;
  }) => void;
  /** Open an existing PLAIN plan (no Takt section) on its WBS anchor. */
  onOpenPlainPlan: (anchorWbsId: number, draftId: number) => void;
}

/** A grouped card: either a Takt section (off the spine) or a plain plan. */
type CardEntry =
  | { key: string; kind: "takt"; summary: SectionCardSummary }
  | { key: string; kind: "plain"; summary: SectionCardSummary; draftId: number; anchorWbsId: number };

const PullPlanSectionsTab: React.FC<Props> = ({
  scheduleId,
  refreshNonce,
  onStartPull,
  onContinuePull,
  onLeafPlanCreated,
  onOpenPlainPlan,
}) => {
  const [board, setBoard] = React.useState<TaktBoardResponse | null>(null);
  const [spine, setSpine] = React.useState<PullPlanTaktSectionRef[] | null>(null);
  const [drafts, setDrafts] = React.useState<Record<string, TaktSectionDraftRef>>({});
  const [plainPlans, setPlainPlans] = React.useState<PullPlan[]>([]);
  const [tree, setTree] = React.useState<PullPlanWBSNode[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [refetchNonce, setRefetchNonce] = React.useState(0);
  // Discard goes through a confirmation modal before deleting.
  const [discardTargetId, setDiscardTargetId] = React.useState<number | null>(null);
  const [discarding, setDiscarding] = React.useState(false);

  const handleDiscardDraft = React.useCallback((draftId: number) => {
    setDiscardTargetId(draftId);
  }, []);

  const confirmDiscard = React.useCallback(async () => {
    if (discardTargetId == null) return;
    setDiscarding(true);
    try {
      await deletePullPlan(scheduleId, discardTargetId);
      setDiscardTargetId(null);
      setRefetchNonce((n) => n + 1);
    } catch (e: unknown) {
      setError(apiErrorMessage(e, "Failed to discard the draft."));
    } finally {
      setDiscarding(false);
    }
  }, [scheduleId, discardTargetId]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchTaktBoard(scheduleId),
      fetchPullPlanTaktSections(scheduleId),
      fetchLatestDraftsByTaktSection(scheduleId).catch(() => ({})),
      fetchPullPlanWBSTree(scheduleId).catch(() => null),
      listPullPlans(scheduleId, { status: "draft" })
        .then((summaries) =>
          Promise.all(summaries.map((s) => getPullPlan(scheduleId, s.id))),
        )
        .catch(() => [] as PullPlan[]),
    ])
      .then(([boardData, spineData, draftMap, treeData, fullPlans]) => {
        if (cancelled) return;
        setBoard(boardData);
        setSpine(spineData);
        setDrafts(draftMap);
        setTree(treeData);
        setPlainPlans(fullPlans);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message ?? "Failed to load sections.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId, refreshNonce, refetchNonce]);

  // The full section list is the Takt_SectionName spine (so an emptied section
  // still appears); the Takt board only enriches the sections it carries with
  // live zone/date detail. Plain (non-Takt) plans aren't on the spine — they're
  // merged in from listPullPlans, grouped under their anchor's WBS root.
  const { groups, total } = React.useMemo(() => {
    if (!spine) return { groups: [], total: 0 };
    const boardByName = new Map<string, TaktSection>();
    if (board) for (const s of board.sections) boardByName.set(s.name, s);
    const workdays = board?.workdays ?? [];
    const orderedKeys: string[] = [];
    const buckets = new Map<string, CardEntry[]>();
    const ensure = (key: string): CardEntry[] => {
      let b = buckets.get(key);
      if (!b) {
        orderedKeys.push(key);
        b = [];
        buckets.set(key, b);
      }
      return b;
    };

    for (const ref of spine) {
      const boardSection = boardByName.get(ref.name);
      const summary = boardSection
        ? summarizeSection(boardSection, workdays, ref)
        : emptySummaryFromRef(ref);
      ensure(summary.wbsRoot ?? "Other").push({
        key: `takt:${ref.name}`,
        kind: "takt",
        summary,
      });
    }

    let plainCount = 0;
    for (const card of plainPlanCards(plainPlans, tree)) {
      plainCount += 1;
      ensure(card.groupName).push({
        key: `plan:${card.draftId}`,
        kind: "plain",
        summary: card.summary,
        draftId: card.draftId,
        anchorWbsId: card.anchorWbsId,
      });
    }

    return {
      groups: orderedKeys.map((name) => ({ name, items: buckets.get(name)! })),
      total: spine.length + plainCount,
    };
  }, [spine, board, plainPlans, tree]);

  // A WBS root with a single section (Mock-ups, TCO, …) shouldn't get its own
  // header + lone-card row; collect those loners and flow them together at the
  // end. Headers stay for the real multi-section roots.
  const multiGroups = React.useMemo(
    () => groups.filter((g) => g.items.length > 1),
    [groups],
  );
  const loneItems = React.useMemo(
    () => groups.filter((g) => g.items.length === 1).flatMap((g) => g.items),
    [groups],
  );

  const renderCard = (card: CardEntry) => {
    if (card.kind === "plain") {
      return (
        <SectionCard
          key={card.key}
          summary={card.summary}
          kind="plain"
          latestDraftId={card.draftId}
          onStartPull={() => onOpenPlainPlan(card.anchorWbsId, card.draftId)}
          onContinuePull={() => onOpenPlainPlan(card.anchorWbsId, card.draftId)}
          onDiscardDraft={handleDiscardDraft}
        />
      );
    }
    const draft = drafts[card.summary.name] ?? null;
    return (
      <SectionCard
        key={card.key}
        summary={card.summary}
        latestDraftId={draft ? draft.id : null}
        onStartPull={(mode) => onStartPull(card.summary.name, mode)}
        onContinuePull={(draftId) => onContinuePull(card.summary.name, draftId)}
        onDiscardDraft={handleDiscardDraft}
      />
    );
  };

  return (
    <div className="pull-plan-sections-tab">
      {error && <div className="pull-plan-sections-tab-error">{error}</div>}
      <div className="pull-plan-sections-tab-toolbar">
        <button
          type="button"
          className="pull-plan-sections-tab-leaves-btn"
          onClick={() => setPanelOpen((v) => !v)}
        >
          + New plan from WBS
        </button>
      </div>
      {panelOpen && (
        <NewPlanFromWbsPanel
          scheduleId={scheduleId}
          onCancel={() => setPanelOpen(false)}
          onCreated={(args) => {
            setPanelOpen(false);
            onLeafPlanCreated(args);
          }}
        />
      )}
      <Spinner visible={loading} label="Loading sections..." />
      {!loading && total === 0 && !error && (
        <div className="pull-plan-sections-tab-empty">
          No sections or plans yet — use “+ New plan from WBS” to start one.
        </div>
      )}
      {multiGroups.map((group) => (
        <section className="pull-plan-sections-tab-group" key={group.name}>
          <h3 className="pull-plan-sections-tab-group-header">{group.name}</h3>
          <div className="pull-plan-sections-tab-list">
            {group.items.map(renderCard)}
          </div>
        </section>
      ))}
      {loneItems.length > 0 && (
        <div className="pull-plan-sections-tab-list pull-plan-sections-tab-lone">
          {loneItems.map(renderCard)}
        </div>
      )}
      {discardTargetId != null && (
        <DiscardDraftModal
          onCancel={() => setDiscardTargetId(null)}
          onConfirm={confirmDiscard}
          discarding={discarding}
        />
      )}
    </div>
  );
};

export default PullPlanSectionsTab;
